import { useNavigate } from "@tanstack/react-router";
import { Package } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

interface RelatedOrdersBannerProps {
  saleOrderId: string;
  relatedOrderIds: string[] | undefined;
}

/**
 * Surfaces the backend's related_order_ids pointer (spec §5) as a banner with
 * one link per sibling package. Deliberately NOT named SplitBanner/
 * SplitOrdersBanner — that naming belongs to the unrelated WMS "split
 * shipment label" feature (SplitOrdersPage/SplitDetailPage). Also
 * independent of the dormant splitIndex/splitTotal/split_of badge, which is a
 * different, dead mechanism this does not touch.
 */
export function RelatedOrdersBanner({
  saleOrderId,
  relatedOrderIds,
}: RelatedOrdersBannerProps): JSX.Element | null {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();

  if (!relatedOrderIds || relatedOrderIds.length === 0) {
    return null;
  }

  const totalPackages = relatedOrderIds.length + 1;
  // The translated sentence embeds `saleOrderId` inline via {{saleOrderId}}
  // interpolation, but tests (and the DOM) need the order id addressable as
  // its own node rather than buried inside one long text run. Split the
  // interpolated string on the id itself and re-wrap it in its own <span>
  // without changing the translated copy.
  const bannerText = t("related_orders_banner_text", { n: totalPackages, saleOrderId });
  const [beforeId, afterId] = bannerText.split(saleOrderId);

  return (
    <div
      data-testid="related-orders-banner"
      className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--intent-info-border)] bg-[var(--intent-info-bg)] px-4 py-3 text-sm text-[var(--intent-info-fg)]"
    >
      <Package className="h-4 w-4 shrink-0" />
      <span>
        {beforeId}
        <span>{saleOrderId}</span>
        {afterId}
      </span>
      <div className="flex flex-wrap gap-2">
        {relatedOrderIds.map((siblingId, i) => (
          <button
            key={siblingId}
            type="button"
            className="rounded-md border border-[var(--intent-info-border)] px-2 py-1 font-medium underline-offset-2 hover:underline"
            onClick={() =>
              void navigate({
                to: "/orders/$saleOrderId",
                params: { saleOrderId },
                search: { order_id: siblingId },
              })
            }
          >
            {t("related_orders_view_package", { index: i + 2 })}
          </button>
        ))}
      </div>
    </div>
  );
}
